import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { BrandLockup } from "@/components/landing/LogoMark";
import { platformClient } from "./platformApi";

const euro = (v) => `€ ${Number(v || 0).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function QuoteAccept() {
    const { token } = useParams();
    const [doc, setDoc] = useState(null);
    const [error, setError] = useState(false);
    const [accepted, setAccepted] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await platformClient.get(`/public/aanbod/${token}`);
                setDoc(data);
                if (data.status === "akkoord") setAccepted(true);
            } catch {
                setError(true);
            }
        })();
    }, [token]);

    const accept = async () => {
        setBusy(true);
        try {
            await platformClient.post(`/public/aanbod/${token}/accept`);
            setAccepted(true);
        } catch {} finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="quote-accept-page" className="min-h-screen bg-ink px-6 py-12 text-bone">
            <div className="noise-overlay" aria-hidden="true" />
            <div className="mx-auto max-w-2xl">
                <BrandLockup emblemClass="h-12 w-12" textClass="text-xl" align="start" />

                {error ? (
                    <div className="mt-16 border border-white/10 bg-coal p-10 text-center">
                        <p data-testid="quote-invalid" className="font-display text-2xl">Link ongeldig of verlopen</p>
                        <p className="mt-3 text-sm text-ash">Vraag Clarke Business Solutions om een nieuwe offerte-link.</p>
                    </div>
                ) : !doc ? (
                    <p className="mt-16 text-xs uppercase tracking-[0.3em] text-ash">Laden…</p>
                ) : (
                    <div className="mt-10 border border-white/10 bg-coal">
                        <div className="border-b border-white/10 p-8">
                            <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Offerte {doc.number}</p>
                            <h1 className="mt-3 font-display text-3xl tracking-tight">Clarke Business Solutions</h1>
                            <p className="mt-2 text-sm text-ash">Voor {doc.tenant_name} · {doc.issue_date}</p>
                            {doc.plan_label ? (
                                <div className="mt-6 grid grid-cols-2 gap-4">
                                    <div className="border border-gold/30 bg-gold/5 p-4">
                                        <p className="text-[10px] uppercase tracking-[0.2em] text-ash">Abonnement</p>
                                        <p data-testid="quote-plan" className="mt-2 font-display text-xl text-gold">{doc.plan_label}</p>
                                        <p className="mt-1 text-xs text-ash">{euro(doc.monthly_price)} per maand</p>
                                    </div>
                                    <div className="border border-white/10 p-4">
                                        <p className="text-[10px] uppercase tracking-[0.2em] text-ash">Eenmalige onboarding</p>
                                        <p data-testid="quote-setup" className="mt-2 font-display text-xl text-bone">{euro(doc.setup_fee)}</p>
                                        <p className="mt-1 text-xs text-ash">inrichting, data-import en training</p>
                                    </div>
                                </div>
                            ) : null}
                        </div>

                        <div className="p-8">
                            <div className="space-y-3">
                                {doc.items.map((item, i) => (
                                    <div key={i} className="flex items-center justify-between text-sm">
                                        <span className="text-bone">{item.description}</span>
                                        <span className="text-ash">
                                            {item.quantity} × {euro(item.unit_price)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-6 space-y-2 border-t border-white/10 pt-5 text-sm">
                                <div className="flex justify-between text-ash">
                                    <span>Subtotaal</span>
                                    <span>{euro(doc.subtotal)}</span>
                                </div>
                                <div className="flex justify-between text-ash">
                                    <span>BTW {doc.vat_rate}%</span>
                                    <span>{euro(doc.vat_amount)}</span>
                                </div>
                                <div className="flex justify-between text-base text-bone">
                                    <span>Eenmalig totaal</span>
                                    <span data-testid="quote-total" className="text-gold">{euro(doc.total)}</span>
                                </div>
                            </div>
                            {doc.notes ? <p className="mt-6 text-xs leading-relaxed text-ash">{doc.notes}</p> : null}
                            <p className="mt-6 text-[11px] leading-relaxed text-ash/70">
                                Maandelijks opzegbaar na de initiële looptijd van 12 maanden. Bij akkoord ontvang je de
                                SaaS-overeenkomst en wordt je omgeving ingericht. De verwerkersovereenkomst is van
                                toepassing (clarke-process-automation.com/verwerkersovereenkomst).
                            </p>
                        </div>

                        <div className="border-t border-white/10 p-8">
                            {accepted ? (
                                <div data-testid="quote-accepted" className="flex items-center gap-4 border border-gold/40 bg-gold/10 p-6">
                                    <CheckCircle2 className="h-8 w-8 shrink-0 text-gold" />
                                    <div>
                                        <p className="font-display text-xl text-gold">Welkom aan boord!</p>
                                        <p className="mt-1 text-sm text-ash">
                                            Je abonnement is actief. Je ontvangt zo een bevestiging en je inloggegevens per e-mail.
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <button
                                    data-testid="quote-accept-button"
                                    onClick={accept}
                                    disabled={busy}
                                    className="w-full bg-gold px-8 py-4 text-xs uppercase tracking-[0.25em] text-black transition-colors hover:bg-bone disabled:opacity-60"
                                >
                                    {busy ? "Bezig…" : "Akkoord — start mijn abonnement"}
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
